/**
 * OutputPackageView — the output_package "ship-gate" board (Model 3, Step 3).
 *
 * The package no longer holds the source of truth for review: each artifact is
 * read + approved in its HOME workspace (model-derived → the production
 * "Generated documents" tab; narrative → its documentation/research workspace;
 * the report → production/research). This view is a flat READINESS board: it
 * shows each referenced artifact's LIVE status (approved / changed → re-review /
 * needs review / missing) read from its home, plus the runnable model(s) and the
 * single gated "Release & export". No document bodies, no per-doc approve here.
 *
 * Readiness comes from GET /workspace/{id}/readiness (resolves the package's
 * document_refs against their home workspaces).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useProjectStore } from '../../stores/projectStore';
import api, {
  exportOutputPackageZip,
  getPackageReadiness,
  PackageReadinessRow,
  type PackageReadinessResult,
} from '../../apiService';
import type { WorkspaceMode } from '../../types/workspace';
import { formatCount } from '../../utils/plural';
import {
  Package,
  CheckCircle2,
  Clock,
  AlertTriangle,
  CircleDashed,
  Play,
  Download,
  AlertCircle,
  Loader2,
  ExternalLink,
} from 'lucide-react';

interface OutputPackageViewProps {
  projectId: string | null;
  workspaceId: string | null;
  authToken?: string;
  userId?: string;
}

const GROUP_LABEL: Record<string, string> = {
  model_derived: 'Model-derived',
  narrative: 'Narrative',
  report: 'Report',
  other: 'Other',
};
const GROUP_ORDER = ['model_derived', 'narrative', 'report', 'other'];

/** A row is blocking unless it's approved — except a PRESENT report, whose
 *  sign-off is the package Release attestation itself (Step 4). A missing report
 *  still blocks (no deliverable). */
function isBlocking(row: PackageReadinessRow): boolean {
  if (row.status === 'approved') return false;
  if (row.status === 'missing') return true;
  return row.source_kind !== 'report';
}

function StatusChip({ status, modelMoved }: { status: PackageReadinessRow['status']; modelMoved?: boolean }) {
  if (status === 'approved') {
    // Approved, but the model it was derived from has moved on since — a
    // distinct re-review signal (the content itself is unchanged, so it isn't
    // "stale", but it no longer reflects the current model).
    if (modelMoved) {
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-warning">
          <AlertTriangle className="w-4 h-4" /> Approved · model moved → re-review
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-success">
        <CheckCircle2 className="w-4 h-4" /> Approved
      </span>
    );
  }
  if (status === 'stale') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-warning">
        <AlertTriangle className="w-4 h-4" /> Changed → re-review
      </span>
    );
  }
  if (status === 'unapproved') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-warning">
        <Clock className="w-4 h-4" /> Needs review
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-fg-tertiary">
      <CircleDashed className="w-4 h-4" /> Missing
    </span>
  );
}

export default function OutputPackageView({
  projectId,
  workspaceId,
  authToken,
  userId,
}: OutputPackageViewProps) {
  const dictWorkspace = useProjectStore((s) => s.dictWorkspace);
  const loadSpecificWorkspace = useProjectStore((s) => s.loadSpecificWorkspace);
  const setWorkspaceMode = useProjectStore((s) => s.setWorkspaceMode);

  const [readiness, setReadiness] = useState<PackageReadinessRow[] | null>(null);
  const [models, setModels] = useState<Array<Record<string, unknown>>>([]);
  const [packageArchive, setPackageArchive] = useState<PackageReadinessResult['package_archive']>(null);
  const [loading, setLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [runStatus, setRunStatus] = useState<string | null>(null);
  const [runningModelId, setRunningModelId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const meta = dictWorkspace?.output_package_meta ?? null;
  const hasPackage = !!meta || (readiness?.length ?? 0) > 0;

  const fetchReadiness = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    setError(null);
    const res = await getPackageReadiness(workspaceId, authToken);
    if (res.success) {
      setReadiness(res.readiness ?? []);
      setModels(res.models ?? []);
      setPackageArchive(res.package_archive ?? null);
    } else {
      setError(res.error || 'Failed to load readiness');
      setReadiness([]);
      setPackageArchive(null);
    }
    setLoading(false);
  }, [workspaceId, authToken]);

  useEffect(() => {
    setPackageArchive(null);
    void fetchReadiness();
  }, [fetchReadiness]);

  // Best-effort cross-workspace navigation: source_kind → the home's view mode.
  const handleOpen = async (row: PackageReadinessRow) => {
    if (!row.home_workspace_id || !userId) return;
    const loaded = await loadSpecificWorkspace(row.home_workspace_id, userId);
    if (!loaded) return;
    const mode: WorkspaceMode =
      row.source_kind === 'narrative' ? 'documentation' : 'production';
    setWorkspaceMode(mode, 'user');
  };

  const handleExport = async () => {
    if (!workspaceId) return;
    setError(null);
    setIsExporting(true);
    try {
      const res = await exportOutputPackageZip(workspaceId, userId ?? '', authToken);
      if (!res.success) {
        setError(res.error || 'Failed to export');
      } else {
        await fetchReadiness();
      }
    } finally {
      setIsExporting(false);
    }
  };

  const handleRun = async (modelId?: string) => {
    if (!projectId || !modelId || !authToken) return;
    setError(null);
    setRunningModelId(modelId);
    setRunStatus(null);
    try {
      const result = await api.executeModel(projectId, modelId, authToken, userId);
      setRunStatus(`Run started${result?.run_id ? ` (${result.run_id})` : ''}. Results live with the run, not this package.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to start run');
    } finally {
      setRunningModelId(null);
    }
  };

  const rows = readiness ?? [];
  const ownedRows = rows; // owned-elsewhere split is a Step 5 refinement
  const blocking = ownedRows.filter(isBlocking);
  const approvedCount = ownedRows.filter((r) => r.status === 'approved').length;
  const ready = ownedRows.length > 0 && blocking.length === 0;

  // Group rows for display.
  const grouped: Record<string, PackageReadinessRow[]> = {};
  for (const r of rows) {
    const key = GROUP_ORDER.includes(r.source_kind || '') ? (r.source_kind as string) : 'other';
    (grouped[key] = grouped[key] || []).push(r);
  }

  return (
    <div className="h-full overflow-y-auto p-6 bg-bg-warm">
      <div className="flex items-center gap-2 mb-6">
        <Package className="w-5 h-5 text-secondary" />
        <h2 className="text-lg font-semibold text-fg">Output package</h2>
        {hasPackage && rows.length > 0 && (
          <span className="ml-auto text-xs font-medium text-fg-secondary bg-surface border border-border-default px-2 py-0.5 rounded-full">
            {approvedCount}/{ownedRows.length} approved &amp; current
          </span>
        )}
      </div>

      {error && (
        <div className="mb-4 flex items-start gap-2 p-3 bg-surface rounded border border-danger/40 text-sm text-danger">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {loading && readiness === null ? (
        <div className="flex items-center gap-2 text-sm text-fg-tertiary">
          <Loader2 className="w-4 h-4 animate-spin" /> Reading readiness from the source workspaces…
        </div>
      ) : !hasPackage ? (
        <div className="text-sm text-fg-tertiary italic">
          No package yet. Assemble the deliverable to reference the model
          documentation and the report here.
        </div>
      ) : (
        <div className="space-y-6">
          {/* Runnable model(s) */}
          {models.length > 0 && (
            <div>
              <div className="text-xs font-medium text-fg-tertiary uppercase tracking-wide mb-2">
                {models.length === 1 ? 'Runnable model' : `Runnable models (${models.length})`}
              </div>
              <div className="space-y-2">
                {models.map((model) => {
                  const modelId = String(model.model_id ?? '');
                  const version = model.model_version;
                  return (
                    <div key={modelId} className="p-3 bg-surface rounded-lg border border-border-default flex items-center justify-between">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-fg-secondary truncate">
                          {String(model.name || modelId)}
                        </div>
                        <div className="text-xs text-fg-tertiary">
                          {modelId}
                          {version != null && ` · v${version}`}
                        </div>
                      </div>
                      <button
                        onClick={() => handleRun(modelId)}
                        disabled={runningModelId === modelId || !modelId || !projectId || !authToken}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-on-primary text-sm font-medium rounded-md hover:bg-primary-hover disabled:opacity-50 transition-colors flex-shrink-0"
                      >
                        {runningModelId === modelId ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                        Run
                      </button>
                    </div>
                  );
                })}
              </div>
              {runStatus && <div className="text-xs text-success mt-1.5">{runStatus}</div>}
            </div>
          )}

          {/* Readiness board — grouped by source. */}
          <div>
            <div className="text-xs font-medium text-fg-tertiary uppercase tracking-wide mb-2">
              Readiness
            </div>
            {rows.length === 0 ? (
              <div className="p-3 bg-surface rounded-lg border border-border-default text-sm text-fg-tertiary">
                No referenced artifacts yet. Re-assemble the package to record its
                document references.
              </div>
            ) : (
              <div className="space-y-4">
                {GROUP_ORDER.filter((g) => grouped[g]?.length).map((g) => (
                  <div key={g}>
                    <div className="text-[11px] font-semibold text-fg-tertiary mb-1.5">{GROUP_LABEL[g]}</div>
                    <div className="space-y-2">
                      {grouped[g].map((row) => (
                        <div
                          key={row.file_id}
                          className="p-3 bg-surface rounded-lg border border-border-default flex items-center justify-between gap-2"
                        >
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-fg-secondary truncate">
                              {row.title || row.file_id}
                            </div>
                            <div className="text-xs text-fg-tertiary truncate">
                              {row.approved_by
                                ? `approved by ${row.approved_by}${row.approver_role ? ` (${row.approver_role})` : ''}`
                                : row.home_workspace_id
                                  ? 'reviewed in its home workspace'
                                  : row.file_id}
                            </div>
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            <StatusChip status={row.status} modelMoved={row.model_moved} />
                            {row.home_workspace_id && (
                              <button
                                type="button"
                                onClick={() => handleOpen(row)}
                                className="inline-flex items-center gap-1 text-xs font-medium text-link hover:underline"
                                title="Open the home workspace where this artifact is reviewed"
                              >
                                Open <ExternalLink className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Release & export — the single gated freeze action. */}
          <div className="pt-4 border-t border-border-default">
            {packageArchive?.released && (
              <div
                className={`mb-2 text-xs ${packageArchive.release_current ? 'text-success' : 'text-warning'}`}
              >
                {packageArchive.release_current
                  ? 'Released and current'
                  : 'Previous release is stale — release the current package again'}
              </div>
            )}
            <div className="flex items-center gap-3">
              <button
                onClick={handleExport}
                disabled={isExporting || !workspaceId || !ready}
                className="flex items-center gap-1.5 px-4 py-2 bg-primary text-on-primary text-sm font-semibold rounded-lg hover:bg-primary-hover disabled:opacity-50 transition-colors"
              >
                {isExporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                Release &amp; export (.zip)
              </button>
              {!ready && ownedRows.length > 0 && (
                <span className="text-xs text-warning">
                  Blocked: {formatCount(blocking.length, { one: 'artifact', other: 'artifacts' })} not approved &amp; current
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
